import OnboardingPageTitle from "./OnboardingPageTitle";
/* eslint-disable react-hooks/exhaustive-deps -- lookup hydrators intentionally follow selected IDs */
import i18n from "../../i18n";
import { Trans } from "react-i18next";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./currentEmployment.css";
import { resolveApiUrl, resolveOrgApiUrl } from "../../api/api";
import { getAuthenticatedTenantId } from "../../lib/employee";
import sessionDB from "../../utils/sessionDB";
import {
  buildOnboardingAccessMessage,
  isOnboardingFeatureUnavailableError,
  isGenericOnboardingErrorMessage,
  parseOnboardingErrorResponse,
  handleOnboardingApiError,
  resolveOnboardingActionConfig
} from "./onboardingFeatureAccess";
import { format, parse, isBefore } from "date-fns";
import { CalendarIcon, ChevronDown } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";


const ACTION_ORDER = ["add", "search", "update", "hierarchy"];
//const VISIBLE_ACTIONS = ["add", "update", "hierarchy"];
const ROLES_ENDPOINT = "/saas/hrms/org/roles";
const ROLE_BY_ID_ENDPOINT = "/saas/hrms/org/roles";
const CANONICAL_ROLES_ENDPOINT = "/saas/hrms/org/canonical_roles";
const DESIGNATIONS_ENDPOINT = "/saas/hrms/org/designations";
const DESIGNATION_BY_ID_ENDPOINT = "/saas/hrms/org/designations";
const DEPARTMENTS_ENDPOINT = "/saas/hrms/org/departments";
const DEPARTMENT_BY_ID_ENDPOINT = "/saas/hrms/org/departments";
const GRADE_LEVELS_ENDPOINT = "/saas/hrms/org/grade-levels";
const GRADE_LEVEL_BY_ID_ENDPOINT = "/saas/hrms/org/grade-levels";
const DIVISIONS_ENDPOINT = "/saas/hrms/org/divisions";
const DIVISION_BY_ID_ENDPOINT = "/saas/hrms/org/divisions";
const COST_CENTERS_ENDPOINT = "/saas/hrms/org/cost-centers";
const COST_CENTER_BY_ID_ENDPOINT = "/saas/hrms/org/cost-centers";
const WORK_LOCATIONS_ENDPOINT = "/saas/hrms/org/work-locations";
const WORK_LOCATION_BY_ID_ENDPOINT = "/saas/hrms/org/work-locations";
const SKILLS_ENDPOINT = "/saas/hrms/org/skills";
const SKILL_BY_ID_ENDPOINT = "/saas/hrms/org/skills";
const EMP_TYPES_ENDPOINT = "/saas/hrms/org/emp-type";
const EMP_STATUSES_ENDPOINT = "/saas/hrms/org/emp-status";
const EMPLOYEE_BY_CANONICAL_ROLE_ENDPOINT = "/saas/hrms/current-employment/search/canonicalRoleName";
const EMPLOYEE_BY_ROLE_PAGE_SIZE = 10;

const ACTIONS = {
  add: {
    title: "Save Current Employment",
    serviceCode: "HR02A",
    endpoint: "/saas/hrms/current-employment/add",
    method: "POST"
  },
  search: {
    title: "Search",
    serviceCode: "HR02S",
    endpoint: "/saas/hrms/current-employment/search",
    method: "POST"
  },
  update: {
    title: "Update",
    serviceCode: "HR02U",
    endpoint: "/saas/hrms/current-employment/update/{empId}",
    method: "PUT"
  },
  hierarchy: {
    title: "Hierarchy",
    serviceCode: "HR02H",
    endpoint: "/saas/hrms/current-employment/hierarchy/{empId}",
    method: "GET"
  }
};

const EMP_TYPES = ["Probationary", "Permanent", "Contract", "Intern", "Consultant", "Notice_Period"];
const EMP_STATUSES = ["Active", "Inactive", "Terminated", "Onboarding"];

const EMP_TYPE_OPTION_KEYS = {
  Probationary: "onboarding.options.empType.probationary",
  Permanent: "onboarding.options.empType.permanent",
  Contract: "onboarding.options.empType.contract",
  Intern: "onboarding.options.empType.intern",
  Consultant: "onboarding.options.empType.consultant",
  Notice_Period: "onboarding.options.empType.noticePeriod"
};

const EMP_STATUS_OPTION_KEYS = {
  Active: "onboarding.options.empStatus.active",
  Inactive: "onboarding.options.empStatus.inactive",
  Terminated: "onboarding.options.empStatus.terminated",
  Onboarding: "onboarding.options.empStatus.onboarding"
};

const FIELD_LABEL_KEYS = {
  empId: "common.employeeId",
  empType: "onboarding.employeeType",
  empStatus: "onboarding.labels.employeeStatus",
  canonicalRoleName: "onboarding.labels.canonicalRoleName",
  designationId: "onboarding.labels.designation",
  departmentId: "common.department",
  gradeLevelId: "onboarding.labels.gradeLevel",
  divisionId: "onboarding.labels.division",
  costCenterId: "onboarding.labels.costCenter",
  workLocationId: "onboarding.labels.workLocation",
  dateOfJoin: "onboarding.labels.dateOfJoining",
  terminationDate: "onboarding.labels.terminationDate",
  managerId: "common.manager",
  hrId: "onboarding.labels.hr",
  skillId: "onboarding.labels.skills",
  roleId: "onboarding.labels.role"
};

const LOOKUP_ERROR_KEYS = {
  roles: "onboarding.errors.unableToLoadRoles",
  canonicalRoles: "onboarding.errors.unableToLoadCanonicalRoles",
  skills: "onboarding.errors.unableToLoadSkills",
  gradeLevels: "onboarding.errors.unableToLoadGradeLevels",
  divisions: "onboarding.errors.unableToLoadDivisions",
  costCenters: "onboarding.errors.unableToLoadCostCenters",
  workLocations: "onboarding.errors.unableToLoadWorkLocations",
  designations: "onboarding.errors.unableToLoadDesignations",
  departments: "onboarding.errors.unableToLoadDepartments"
};

function getFieldLabel(name) {
  const key = FIELD_LABEL_KEYS[name];
  return key ? i18n.t(key) : name;
}

function getFieldLabelsMap() {
  return Object.fromEntries(
    Object.keys(FIELD_LABEL_KEYS).map((name) => [name, getFieldLabel(name)])
  );
}

function getRuleMessage(errorKey, fieldName) {
  const fullKey = `onboarding.errors.${errorKey}`;
  return i18n.exists(fullKey)
    ? i18n.t(fullKey)
    : i18n.t("validation.fieldRequired", { field: getFieldLabel(fieldName) });
}

function translateEmpTypeLabel(value) {
  const key = EMP_TYPE_OPTION_KEYS[value];
  return key ? i18n.t(key) : value;
}

function translateEmpStatusLabel(value) {
  const key = EMP_STATUS_OPTION_KEYS[value];
  return key ? i18n.t(key) : value;
}

function translateEmpTypeOptions(options) {
  return options.map((option) => ({
    ...option,
    label: translateEmpTypeLabel(option.value)
  }));
}

function translateEmpStatusOptions(options) {
  return options.map((option) => ({
    ...option,
    label: translateEmpStatusLabel(option.value)
  }));
}

function getLookupErrorMessage(key) {
  const messageKey = LOOKUP_ERROR_KEYS[key];
  return messageKey ? i18n.t(messageKey) : i18n.t("onboarding.errors.currentEmploymentRequestFailed");
}

const INITIAL_FORM = {
  empId: "",
  empType: "",
  empStatus: "",
  roleId: "",
  canonicalRoleName: "",
  skillId: "",
  designationId: "",
  departmentId: "",
  gradeLevelId: "",
  divisionId: "",
  costCenterId: "",
  workLocationId: "",
  loginBasedOnGps: "N",
  managerId: "",
  managerName: "",
  hrId: "",
  hrName: "",
  dateOfJoin: "",
  terminationDate: ""
};

/*function getPersistedCurrentEmploymentDraft() {
  const draft = sessionDB.getSession().onboardingCurrentEmploymentDraft;
  return draft && typeof draft === "object" ? draft : {};
}*/

const REQUIRED_BY_ACTION = {
  add: ["roleId", "canonicalRoleName", "hrId", "managerId"],
  update: ["roleId", "canonicalRoleName", "hrId", "managerId"],
  search: [],
  hierarchy: ["empId"]
};

const RULES = {
  empId: (value) => (!value.trim() ? "" : !/^\d+$/.test(value.trim()) ? getRuleMessage("empIdMustBeNumeric", "empId") : ""),
  // Employee type/status options are now loaded from /org/emp-type/ and
  // /org/emp-status/, so the valid set is dynamic. Presence (required) is
  // enforced separately; no static membership check here.
  empType: () => "",
  empStatus: () => "",
  roleId: () => "",
  canonicalRoleName: (value) => (!value ? "" : value.trim().length > 100 ? i18n.t("onboarding.errors.max100Characters") : ""),
  skillId: (value) => (!value ? "" : value.trim().length > 150 ? i18n.t("onboarding.errors.max150Characters") : ""),
  designationId: (value) => (!value ? "" : !/^\d+$/.test(value.trim()) ? getRuleMessage("chooseValidDesignation", "designationId") : ""),
  departmentId: (value) => (!value ? "" : !/^\d+$/.test(value.trim()) ? getRuleMessage("chooseValidDepartment", "departmentId") : ""),
  gradeLevelId: (value) => (!value ? "" : !/^\d+$/.test(value.trim()) ? getRuleMessage("chooseValidGradeLevel", "gradeLevelId") : ""),
  divisionId: (value) => (!value ? "" : !/^\d+$/.test(value.trim()) ? getRuleMessage("chooseValidDivision", "divisionId") : ""),
  costCenterId: (value) => (!value ? "" : !/^\d+$/.test(value.trim()) ? getRuleMessage("chooseValidCostCenter", "costCenterId") : ""),
  workLocationId: (value) => (!value ? "" : !/^\d+$/.test(value.trim()) ? getRuleMessage("chooseValidLocation", "workLocationId") : ""),
  managerId: (value) => (!value ? "" : !/^\d+$/.test(value.trim()) ? getRuleMessage("managerMustBeNumeric", "managerId") : ""),
  hrId: (value) => (!value ? "" : !/^\d+$/.test(value.trim()) ? getRuleMessage("hrMustBeNumeric", "hrId") : ""),
  dateOfJoin: (value) => (!value ? "" : !/^\d{2}-\d{2}-\d{4}$/.test(value.trim()) ? getRuleMessage("useDdMmYyyy", "dateOfJoin") : ""),
  terminationDate: (value, allValues) => {
    if (!value) return "";
    if (!/^\d{2}-\d{2}-\d{4}$/.test(value.trim())) return getRuleMessage("useDdMmYyyy", "terminationDate");
    const joinValue = allValues && allValues.dateOfJoin;
    if (joinValue && /^\d{2}-\d{2}-\d{4}$/.test(joinValue.trim())) {
      const termination = parse(value.trim(), "dd-MM-yyyy", new Date());
      const joining = parse(joinValue.trim(), "dd-MM-yyyy", new Date());
      if (isBefore(termination, joining)) return getRuleMessage("terminationBeforeJoining", "terminationDate");
    }
    return "";
  }
};

function buildUrl(template, form) {
  return template.replace("{empId}", encodeURIComponent(form.empId.trim()));
}

function getNextAction(currentAction) {
  const currentIndex = ACTION_ORDER.indexOf(currentAction);
  return ACTION_ORDER[(currentIndex + 1) % ACTION_ORDER.length];
}

function formatDisplayDateInput(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
}

function displayDateToApiDate(value) {
  if (!/^\d{2}-\d{2}-\d{4}$/.test(String(value || "").trim())) return undefined;
  const [day, month, year] = value.trim().split("-");
  return `${year}-${month}-${day}`;
}

function normalizeEmploymentErrorMessage(message) {
  const cleaned = String(message || "").trim();
  if (!cleaned) return i18n.t("onboarding.errors.currentEmploymentRequestFailed");

  return cleaned
    .replace(/currentent/gi, "current")
    .replace(/employement/gi, "employment")
    .replace(/Validation Failed\s*-\s*current employment/i, i18n.t("onboarding.errors.currentEmploymentRequestFailed"));
}

/* eslint-disable-next-line no-unused-vars -- retained for normalized API error handling */
function extractEmploymentErrorMessage(body, fallback) {
  if (!body || typeof body !== "object") {
    return normalizeEmploymentErrorMessage(fallback);
  }

  const direct = [body.message, body.error, body.data]
    .find((value) => typeof value === "string" && value.trim());
  if (direct) {
    return normalizeEmploymentErrorMessage(direct);
  }

  const objectCollections = [body.data, body.errors, body.validationErrors, body.fieldErrors];
  for (const collection of objectCollections) {
    if (!collection || Array.isArray(collection) || typeof collection !== "object") {
      continue;
    }

    const parts = Object.entries(collection)
      .map(([field, message]) => {
        if (typeof message === "string" && message.trim()) {
          return `${field}: ${message.trim()}`;
        }
        if (Array.isArray(message) && message.length > 0) {
          return `${field}: ${message.map((item) => String(item || "").trim()).filter(Boolean).join(", ")}`;
        }
        return "";
      })
      .filter(Boolean);

    if (parts.length) {
      return normalizeEmploymentErrorMessage(parts.join(" | "));
    }
  }

  const arrayCollections = [body.details, body.errors, body.validationErrors, body.fieldErrors, body.data?.errors];
  for (const collection of arrayCollections) {
    if (!Array.isArray(collection) || collection.length === 0) {
      continue;
    }

    const parts = collection
      .map((entry) => {
        if (typeof entry === "string" && entry.trim()) {
          return entry.trim();
        }
        if (entry && typeof entry === "object") {
          return (
            entry.message ||
            entry.defaultMessage ||
            entry.error ||
            [entry.field, entry.reason].filter(Boolean).join(": ") ||
            ""
          );
        }
        return "";
      })
      .filter(Boolean);

    if (parts.length) {
      return normalizeEmploymentErrorMessage(parts.join(" | "));
    }
  }

  return normalizeEmploymentErrorMessage(fallback);
}

function normalizeLookupOptions(payload, idKeys = [], labelKeys = []) {
  const candidates = [
    payload,
    payload?.data,
    payload?.content,
    payload?.result,
    payload?.data?.content,
    payload?.result?.content,
    payload?.data?.result,
    payload?.data?.result?.content,
    payload?.records,
    payload?.items,
    payload?.rows,
    payload?.roles,
    payload?.designations,
    payload?.departments,
    payload?.gradeLevels,
    payload?.divisions,
    payload?.costCenters,
    payload?.skills,
    payload?.data?.roles,
    payload?.data?.designations,
    payload?.data?.departments,
    payload?.data?.gradeLevels,
    payload?.data?.divisions,
    payload?.data?.costCenters,
    payload?.data?.skills,
    payload?.data?.records,
    payload?.data?.items,
    payload?.data?.rows
  ];

  const list = candidates.find((value) => Array.isArray(value));
  if (!Array.isArray(list)) return [];

  return list
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const id = [
        ...idKeys.map((key) => item?.[key]),
        item?.role_id,
        item?.designation_id,
        item?.department_id,
        item?.grade_level_id,
        item?.division_id,
        item?.cost_center_id,
        item?.skill_id
      ].find((value) => value !== null && value !== undefined && String(value).trim());
      const label = [
        ...labelKeys.map((key) => item?.[key]),
        item?.role_name,
        item?.designation_name,
        item?.department_name,
        item?.grade_level_name,
        item?.division_name,
        item?.cost_center_name,
        item?.skill_name,
        item?.description,
        item?.title
      ].find((value) => value !== null && value !== undefined && String(value).trim());
      if (!id || !label) return null;
      return { value: String(id).trim(), label: String(label).trim() };
    })
    .filter(Boolean);
}

// Enum-style lookups (emp type / emp status) may come back either as a plain
// array of strings (["Permanent", ...]) or as objects. Unlike the id-based
// org lookups, the VALUE we send back to the API is the enum string itself,
// so value and label both resolve to that string (label prefers a nicer
// description when present). Falls back gracefully to [] on unexpected shapes.
function normalizeEnumOptions(payload, valueKeys = [], labelKeys = []) {
  const candidates = [
    payload,
    payload?.data,
    payload?.content,
    payload?.result,
    payload?.data?.content,
    payload?.records,
    payload?.items,
    payload?.rows,
    payload?.data?.records
  ];
  const list = candidates.find((value) => Array.isArray(value));
  if (!Array.isArray(list)) return [];

  return list
    .map((item) => {
      if (typeof item === "string") {
        const str = item.trim();
        return str ? { value: str, label: str } : null;
      }
      if (!item || typeof item !== "object") return null;
      const value = [
        ...valueKeys.map((key) => item?.[key]),
        item?.name,
        item?.code,
        item?.value,
        item?.id
      ].find((entry) => entry !== null && entry !== undefined && String(entry).trim());
      if (value === null || value === undefined || !String(value).trim()) return null;
      const label = [
        ...labelKeys.map((key) => item?.[key]),
        item?.description,
        item?.label,
        item?.name,
        value
      ].find((entry) => entry !== null && entry !== undefined && String(entry).trim());
      return { value: String(value).trim(), label: String(label ?? value).trim() };
    })
    .filter(Boolean);
}

function extractLookupLabel(payload, labelKeys = []) {
  if (!payload || typeof payload !== "object") return "";
  const source = Array.isArray(payload) ? payload[0] : payload;
  const value = [
    ...labelKeys.map((key) => source?.[key]),
    source?.role_name,
    source?.designation_name,
    source?.department_name,
    source?.grade_level_name,
    source?.division_name,
    source?.cost_center_name,
    source?.skill_name,
    source?.description,
    source?.title
  ].find((entry) => entry !== null && entry !== undefined && String(entry).trim());
  return value ? String(value).trim() : "";
}

function parseMultiValueIds(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function unwrapEmployeeRolePage(payload) {
  const root = payload?.data ?? payload ?? {};
  const candidates = [
    root?.content,
    root?.data?.content,
    root?.result?.content,
    root?.records,
    root?.items,
    root?.employees,
    Array.isArray(root?.data) ? root.data : null,
    Array.isArray(root) ? root : null,
    Array.isArray(payload) ? payload : null
  ];
  const content = candidates.find((entry) => Array.isArray(entry)) || [];
  const pageNumber = Number(
    root?.number ?? root?.page ?? root?.pageNumber ?? root?.data?.number ?? 0
  );
  const totalPages = root?.totalPages ?? root?.data?.totalPages;
  const last = root?.last ?? root?.data?.last;
  const hasMore =
    last === false ||
    (Number.isFinite(Number(totalPages)) && pageNumber + 1 < Number(totalPages)) ||
    content.length >= EMPLOYEE_BY_ROLE_PAGE_SIZE;
  return { content, page: Number.isFinite(pageNumber) ? pageNumber : 0, hasMore };
}

function mapEmploymentEmployeeOption(item) {
  if (!item || typeof item !== "object") return null;
  const id = item.empId ?? item.employeeId ?? item.emp_Id ?? item.id;
  if (id == null || String(id).trim() === "") return null;
  const first = item.firstName ?? item.first_name ?? "";
  const last = item.lastName ?? item.last_name ?? "";
  const fullName = `${first} ${last}`.trim();
  const name =
    item.name ||
    item.employeeName ||
    item.empName ||
    item.fullName ||
    fullName ||
    String(id);
  return {
    value: String(id),
    label: String(name).trim() || String(id),
    id: Number(id),
    name: String(name).trim() || String(id)
  };
}

function mergeEmployeeOptions(current = [], next = []) {
  const map = new Map();
  [...current, ...next].forEach((option) => {
    if (!option?.value) return;
    map.set(String(option.value), option);
  });
  return Array.from(map.values());
}

function ensureSelectedEmployeeOption(options, id, name) {
  const value = String(id || "").trim();
  if (!value) return options;
  const label = String(name || "").trim() || value;
  const existing = options.find((option) => option.value === value);
  if (existing) {
    // Prefer a real name over a numeric id placeholder once it becomes available.
    if (!String(name || "").trim() || existing.label === label) return options;
    return options.map((option) => (
      option.value === value
        ? { ...option, label, name: label }
        : option
    ));
  }
  return mergeEmployeeOptions([{ value, label, id: Number(value), name: label }], options);
}

function EmploymentMultiSelect({ id, options, selectedValues, onToggle, onBlur, loading, loadingText, emptyText, error }) {
  const [search, setSearch] = useState("");
  const label = getFieldLabel(id);
  const availableOptions = Array.from(new Map(options.map((option) => [option.value, option])).values());
  selectedValues.forEach((value) => {
    if (!availableOptions.some((option) => option.value === value)) {
      availableOptions.push({ value, label: value });
    }
  });
  const selectedLabels = selectedValues.map((value) =>
    availableOptions.find((option) => option.value === value)?.label || value
  ).join(", ");
  const query = search.trim().toLocaleLowerCase();
  const filteredOptions = availableOptions.filter((option) => option.label.toLocaleLowerCase().includes(query));

  return (
    <Popover onOpenChange={(open) => {
      if (!open) {
        setSearch("");
        onBlur();
      }
    }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          className={`cem-select cem-multiselect-trigger${error ? " error" : ""}`}
          disabled={loading}
          aria-label={`${label}: ${selectedLabels || (loading ? loadingText : i18n.t("onboarding.select"))}`}
          aria-describedby={error ? `${id}-error` : undefined}
          title={selectedLabels}
        >
          <span className="cem-multiselect-value">
            {selectedLabels || (loading ? loadingText : i18n.t("onboarding.select"))}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-2">
        <input data-page-search="true"
          type="search"
          className="cem-input cem-multiselect-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={i18n.t("common.search")}
          aria-label={`${i18n.t("common.search")}: ${label}`}
        />
        <div role="group" aria-label={label} className="cem-multiselect-options">
          {filteredOptions.map((option) => (
            <label key={option.value} className="cem-multiselect-option">
              <input
                type="checkbox"
                checked={selectedValues.includes(option.value)}
                onChange={() => onToggle(option.value)}
                onBlur={onBlur}
              />
              <span>{option.label}</span>
            </label>
          ))}
          {!filteredOptions.length && (
            <p className="cem-skill-empty cem-multiselect-empty">
              {query ? i18n.t("common.noResultsFound", { defaultValue: "No results found" }) : emptyText}
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Field({ id, label, required, error, hint, children, span2 = false }) {
  return (
    <div className={`cem-field${span2 ? " span2" : ""}`}>
      <label className={`cem-label${required ? " required" : ""}`} htmlFor={id}>{label}</label>
      {children}
      {hint ? <p className="cem-hint">{hint}</p> : null}
      <p id={`${id}-error`} className="cem-error" role="alert">{error || ""}</p>
    </div>
  );
}

export default function CurrentEmployment({
  accessToken,
  tenantCode,
  onBack,
  onNext,
  onSuccess,
  breadcrumb,
  initialEmployeeId = "",
  isTabMode = false,
  isActive = true,
  saveTriggerRef
}) {
  useEffect(() => {
    if (saveTriggerRef) {
      saveTriggerRef.current = {
        submit: () => {
          if (action === "search") {
            runSearch();
          } else if (action === "hierarchy") {
            runHierarchy();
          } else {
            submitMutation();
          }
        }
      };
    }
  });
  const [action, setAction] = useState("add");
  const [form, setForm] = useState(() => ({
    ...INITIAL_FORM,
    empId: String(initialEmployeeId || "")
  }));
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [banner, _setBanner] = useState(null);
  const setBanner = (val) => {
    _setBanner((prev) => {
      const next = typeof val === "function" ? val(prev) : val;
      if (next && next.type === "error") {
        if (isOnboardingFeatureUnavailableError(next.msg)) {
          return { type: "error", msg: i18n.t("onboarding.featureUnavailable") };
        }
        if (isGenericOnboardingErrorMessage(next.msg)) {
          return prev;
        }
      }
      return next;
    });
  };
  const [roleOptions, setRoleOptions] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [canonicalRoleOptions, setCanonicalRoleOptions] = useState([]);
  const [canonicalRolesLoading, setCanonicalRolesLoading] = useState(false);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [designationsLoading, setDesignationsLoading] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [gradeLevelOptions, setGradeLevelOptions] = useState([]);
  const [gradeLevelsLoading, setGradeLevelsLoading] = useState(false);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [divisionsLoading, setDivisionsLoading] = useState(false);
  const [costCenterOptions, setCostCenterOptions] = useState([]);
  const [costCentersLoading, setCostCentersLoading] = useState(false);
  const [workLocationOptions, setWorkLocationOptions] = useState([]);
  const [workLocationsLoading, setWorkLocationsLoading] = useState(false);
  const [skillOptions, setSkillOptions] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [empTypeOptions, setEmpTypeOptions] = useState([]);
  const [empTypesLoading, setEmpTypesLoading] = useState(false);
  const [empStatusOptions, setEmpStatusOptions] = useState([]);
  const [empStatusesLoading, setEmpStatusesLoading] = useState(false);
  const [managerOptions, setManagerOptions] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [managersLoadingMore, setManagersLoadingMore] = useState(false);
  const [managerPage, setManagerPage] = useState(0);
  const [managerHasMore, setManagerHasMore] = useState(false);
  const [hrOptions, setHrOptions] = useState([]);
  const [hrsLoading, setHrsLoading] = useState(false);
  const [hrsLoadingMore, setHrsLoadingMore] = useState(false);
  const [hrPage, setHrPage] = useState(0);
  const [hrHasMore, setHrHasMore] = useState(false);
  const managerFetchLockRef = useRef(false);
  const hrFetchLockRef = useRef(false);
  const employeeRoleLookupsLoadedRef = useRef({ manager: false, hr: false });
  const selectedManagerRef = useRef({ id: "", name: "" });
  const selectedHrRef = useRef({ id: "", name: "" });
  const lookupsLoadedRef = useRef({
    roles: false,
    canonicalRoles: false,
    skills: false,
    gradeLevels: false,
    divisions: false,
    costCenters: false,
    workLocations: false,
    designations: false,
    departments: false,
    empTypes: false,
    empStatuses: false
  });
  const employmentLoadedForIdRef = useRef("");
  const requiredFields = useMemo(() => new Set(REQUIRED_BY_ACTION[action] || []), [action]);
  const currentAction = useMemo(() => resolveOnboardingActionConfig({
    submoduleNames: ["Current Employment"],
    action,
    fallback: ACTIONS[action]
  }), [action]);
  const tenantId = sessionDB.getTenantId() || getAuthenticatedTenantId() || "";
  const tenantCodeFromSession = sessionDB.getTenantCode() || tenantCode || "";
  const sessionAccessToken = sessionDB.getAccessToken() || accessToken || "";
  const selectedRoleIds = useMemo(() => parseMultiValueIds(form.roleId), [form.roleId]);
  const selectedSkillIds = useMemo(() => parseMultiValueIds(form.skillId), [form.skillId]);
  const selectedCanonicalRoleNames = useMemo(() => parseMultiValueIds(form.canonicalRoleName), [form.canonicalRoleName]);

  const fetchEmployment = useCallback(async () => {
    if (!initialEmployeeId) return;
    try {
      console.log(initialEmployeeId)
      const response = await fetch(resolveApiUrl(`/saas/hrms/current-employment/search/${initialEmployeeId}`), {
        method: 'GET',
        headers: {
          'x-tenant-code': tenantCodeFromSession || "",
          'x-tenant-id': String(tenantId || ""),
          'x-service-code': 'HR02S',
          authorization: `Bearer ${sessionAccessToken}`
        }
      });

      if (response.ok) {
        const body = await response.json();
        console.log("sghd:",body)
        const data = body.data || body;
        if (data) {
          const rawSkills = data.skillId ?? data.skills ?? data.skillIds ?? "";
          const parsedSkillId = Array.isArray(rawSkills) ? rawSkills.join(",") : String(rawSkills || "");

          const rawGps = data.loginBasedOnGps ?? data.gpsLogin ?? data.isGpsLogin ?? data.gps_login ?? data.gpsLoginEnabled;
          const parsedGps = (rawGps === true || rawGps === "Y" || rawGps === "true" || rawGps === 1) ? "Y" : "N";

          const parsedRole = data.canonicalRoleName || data.roleName || data.canonical_role_name || "";

          setForm({
            empId: String(initialEmployeeId),
            empType: data.empType || "",
            empStatus: data.empStatus || "",
            dateOfJoin: data.dateOfJoin ? format(new Date(data.dateOfJoin), "dd-MM-yyyy") : "",
            probationPeriod: String(data.probationPeriod || ""),
            dateOfConfirmation: data.dateOfConfirmation ? format(new Date(data.dateOfConfirmation), "dd-MM-yyyy") : "",
            roleId: String(data.roleId || data.role_id || data.orgRoleId || ""),
            canonicalRoleName: parsedRole,
            skillId: parsedSkillId,
            designationId: String(data.designationId || data.designation_id || data.orgDesignationId || ""),
            departmentId: String(data.departmentId || data.department_id || data.orgDepartmentId || ""),
            gradeLevelId: String(data.gradeLevelId || data.grade_level_id || data.gradeId || ""),
            divisionId: String(data.divisionId || data.division_id || ""),
            costCenterId: String(data.costCenterId || data.cost_center_id || ""),
            workLocationId: String(data.workLocationId || data.work_location_id || data.locationId || ""),
            loginBasedOnGps: parsedGps,
            managerId: String(data.managerId || data.manager_id || ""),
            managerName: String(
              data.managerName ||
              data.manager_name ||
              data.managerFullName ||
              data.managerEmpName ||
              ""
            ),
            hrId: String(data.hrId || data.hr_id || ""),
            hrName: String(
              data.hrName ||
              data.hr_name ||
              data.hrFullName ||
              data.hrEmpName ||
              ""
            ),
            noticePeriod: String(data.noticePeriod || ""),
            isTerminated: data.isTerminated || false,
            terminationDate: data.terminationDate ? format(new Date(data.terminationDate), "dd-MM-yyyy") : ""
          });
          setAction("update");
        }
      }
    } catch (err) {
      console.error("Failed to load current employment info", err);
    }
  }, [initialEmployeeId, tenantCodeFromSession, tenantId, sessionAccessToken]);

  useEffect(() => {
    if (!initialEmployeeId) {
      setForm({
        ...INITIAL_FORM,
        empId: ""
      });
      setAction("add");
      return;
    }
    setForm((prev) => ({ ...prev, empId: String(initialEmployeeId) }));
  }, [initialEmployeeId]);

  useEffect(() => {
    if (!isActive || !initialEmployeeId) return;
    const empKey = String(initialEmployeeId);
    if (employmentLoadedForIdRef.current === empKey) return;
    employmentLoadedForIdRef.current = empKey;
    fetchEmployment();
  }, [isActive, initialEmployeeId, fetchEmployment]);

  async function fetchOrgLookup(path) {
    if (!tenantId) {
      throw new Error("tenant missing");
    }

    const nextTenantId = String(tenantId || "").trim();
    const nextTenantCode = String(tenantCodeFromSession || "").trim();
    const nextAccessToken = String(sessionAccessToken || "").trim();
    const requestPath = `${path}${path.includes("?") ? "&" : "?"}tenantId=${encodeURIComponent(nextTenantId)}`;
    const headers = {
      Accept: "*/*",
      "x-service-code": "HR20A",
      ...(nextAccessToken ? { Authorization: `Bearer ${nextAccessToken}` } : {}),
      ...(nextTenantId ? { "X-Tenant-Id": nextTenantId } : {}),
      ...(nextTenantCode ? { "X-Tenant-Code": nextTenantCode } : {})
    };

    try {
      const response = await fetch(resolveOrgApiUrl(requestPath), {
        method: "GET",
        headers
      });

      if (!response.ok) {
        throw new Error(String(response.status || "request failed"));
      }

      return await response.json().catch(() => []);
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("request failed");
    }
  }

  async function fetchOrgLookupById(pathTemplate, idValue) {
    if (!tenantId) {
      return null;
    }

    const nextTenantId = String(tenantId || "").trim();
    const nextTenantCode = String(tenantCodeFromSession || "").trim();
    const nextAccessToken = String(sessionAccessToken || "").trim();
    const requestPath = `${pathTemplate}/${encodeURIComponent(String(idValue))}?tenantId=${encodeURIComponent(nextTenantId)}`;
    const headers = {
      Accept: "*/*",
      "x-service-code": "HR20A",
      ...(nextAccessToken ? { Authorization: `Bearer ${nextAccessToken}` } : {}),
      ...(nextTenantId ? { "X-Tenant-Id": nextTenantId } : {}),
      ...(nextTenantCode ? { "X-Tenant-Code": nextTenantCode } : {})
    };

    try {
      const response = await fetch(resolveOrgApiUrl(requestPath), {
        method: "GET",
        headers
      });

      if (!response.ok) {
        return null;
      }

      return await response.json().catch(() => ({}));
    } catch {
      return null;
    }
  }

  const fetchEmployeesByCanonicalRole = useCallback(async (searchTerm, page = 0) => {
    const params = new URLSearchParams({
      searchTerm: String(searchTerm || "").trim(),
      page: String(page),
      size: String(EMPLOYEE_BY_ROLE_PAGE_SIZE),
      sortBy: "first_name",
      sortDir: "ASC"
    });
    const response = await fetch(
      resolveApiUrl(`${EMPLOYEE_BY_CANONICAL_ROLE_ENDPOINT}?${params.toString()}`),
      {
        method: "GET",
        headers: {
          Accept: "*/*",
          "x-tenant-code": tenantCodeFromSession || "",
          "x-tenant-id": String(tenantId || ""),
          "x-service-code": "HR02S",
          authorization: `Bearer ${sessionAccessToken}`
        }
      }
    );
    if (!response.ok) {
      throw new Error(String(response.status || "request failed"));
    }
    const body = await response.json().catch(() => ({}));
    const pageData = unwrapEmployeeRolePage(body);
    return {
      ...pageData,
      content: pageData.content.map(mapEmploymentEmployeeOption).filter(Boolean)
    };
  }, [sessionAccessToken, tenantCodeFromSession, tenantId]);

  selectedManagerRef.current = { id: form.managerId, name: form.managerName };
  selectedHrRef.current = { id: form.hrId, name: form.hrName };

  const loadManagerOptions = useCallback(async (page = 0, { append = false } = {}) => {
    if (!tenantId || managerFetchLockRef.current) return;
    managerFetchLockRef.current = true;
    if (append) setManagersLoadingMore(true);
    else setManagersLoading(true);
    try {
      const result = await fetchEmployeesByCanonicalRole("Manager", page);
      const selected = selectedManagerRef.current;
      setManagerOptions((prev) => {
        const merged = append
          ? mergeEmployeeOptions(prev, result.content)
          : mergeEmployeeOptions(result.content, prev.filter((option) => option.value === String(selected.id)));
        return ensureSelectedEmployeeOption(merged, selected.id, selected.name);
      });
      setManagerPage(result.page ?? page);
      setManagerHasMore(Boolean(result.hasMore));
    } catch {
      if (!append) {
        const selected = selectedManagerRef.current;
        setManagerOptions((prev) => ensureSelectedEmployeeOption(prev, selected.id, selected.name));
        setManagerHasMore(false);
      }
    } finally {
      employeeRoleLookupsLoadedRef.current.manager = true;
      setManagersLoading(false);
      setManagersLoadingMore(false);
      managerFetchLockRef.current = false;
    }
  }, [fetchEmployeesByCanonicalRole, tenantId]);

  const loadHrOptions = useCallback(async (page = 0, { append = false } = {}) => {
    if (!tenantId || hrFetchLockRef.current) return;
    hrFetchLockRef.current = true;
    if (append) setHrsLoadingMore(true);
    else setHrsLoading(true);
    try {
      const result = await fetchEmployeesByCanonicalRole("HR", page);
      const selected = selectedHrRef.current;
      setHrOptions((prev) => {
        const merged = append
          ? mergeEmployeeOptions(prev, result.content)
          : mergeEmployeeOptions(result.content, prev.filter((option) => option.value === String(selected.id)));
        return ensureSelectedEmployeeOption(merged, selected.id, selected.name);
      });
      setHrPage(result.page ?? page);
      setHrHasMore(Boolean(result.hasMore));
    } catch {
      if (!append) {
        const selected = selectedHrRef.current;
        setHrOptions((prev) => ensureSelectedEmployeeOption(prev, selected.id, selected.name));
        setHrHasMore(false);
      }
    } finally {
      employeeRoleLookupsLoadedRef.current.hr = true;
      setHrsLoading(false);
      setHrsLoadingMore(false);
      hrFetchLockRef.current = false;
    }
  }, [fetchEmployeesByCanonicalRole, tenantId]);

  useEffect(() => {
    if (!isActive || !tenantId) return;
    if (!employeeRoleLookupsLoadedRef.current.manager) {
      loadManagerOptions(0);
    }
    if (!employeeRoleLookupsLoadedRef.current.hr) {
      loadHrOptions(0);
    }
  }, [isActive, tenantId, loadManagerOptions, loadHrOptions]);

  useEffect(() => {
    setManagerOptions((prev) => ensureSelectedEmployeeOption(prev, form.managerId, form.managerName));
  }, [form.managerId, form.managerName]);

  useEffect(() => {
    setHrOptions((prev) => ensureSelectedEmployeeOption(prev, form.hrId, form.hrName));
  }, [form.hrId, form.hrName]);

  useEffect(() => {
    if (!form.managerId || form.managerName) return;
    const match = managerOptions.find((option) => option.value === String(form.managerId));
    if (!match?.name) return;
    setForm((prev) => (
      prev.managerId === match.value && !prev.managerName
        ? { ...prev, managerName: match.name }
        : prev
    ));
  }, [form.managerId, form.managerName, managerOptions]);

  useEffect(() => {
    if (!form.hrId || form.hrName) return;
    const match = hrOptions.find((option) => option.value === String(form.hrId));
    if (!match?.name) return;
    setForm((prev) => (
      prev.hrId === match.value && !prev.hrName
        ? { ...prev, hrName: match.name }
        : prev
    ));
  }, [form.hrId, form.hrName, hrOptions]);

  function handleManagerScroll(event) {
    if (!managerHasMore || managersLoadingMore || managersLoading) return;
    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
      loadManagerOptions(managerPage + 1, { append: true });
    }
  }

  function handleHrScroll(event) {
    if (!hrHasMore || hrsLoadingMore || hrsLoading) return;
    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
      loadHrOptions(hrPage + 1, { append: true });
    }
  }



  useEffect(() => {
    // Parent keeps Current Employment mounted while hidden in tab mode.
    // Load org lookups only when this tab/page is active.
    if (!isActive || !tenantId) return;

    let cancelled = false;
    const loaded = lookupsLoadedRef.current;

    async function loadLookup({
      key,
      endpoint,
      setOptions,
      setLoading,
      mapOptions,
      onError,
      cacheOnError = false
    }) {
      if (loaded[key]) return;
      loaded[key] = "loading";
      setLoading(true);
      try {
        const data = await fetchOrgLookup(endpoint);
        if (cancelled) {
          loaded[key] = false;
          return;
        }
        setOptions(mapOptions(data));
        loaded[key] = true;
      } catch (error) {
        if (cancelled) {
          loaded[key] = false;
          return;
        }
        if (onError) {
          onError(error);
        } else {
          setOptions([]);
          setBanner((current) => current || {
            type: "error",
            msg: error.message || getLookupErrorMessage(key)
          });
        }
        loaded[key] = cacheOnError ? true : false;
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    Promise.all([
      loadLookup({
        key: "roles",
        endpoint: ROLES_ENDPOINT,
        setOptions: setRoleOptions,
        setLoading: setRolesLoading,
        mapOptions: (data) => normalizeLookupOptions(data, ["roleId", "id", "value"], ["roleName", "name", "label", "value"]),
        onError: (error) => {
          setRoleOptions([]);
          setBanner((current) => current || {
            type: "error",
            msg: `${i18n.t("onboarding.errors.unableToLoadRoles")} (${error.message || "request failed"}).`
          });
        }
      }),
      loadLookup({
        key: "canonicalRoles",
        endpoint: CANONICAL_ROLES_ENDPOINT,
        setOptions: setCanonicalRoleOptions,
        setLoading: setCanonicalRolesLoading,
        mapOptions: (data) => normalizeLookupOptions(
          data,
          ["canonicalRoleId", "roleId", "id", "value"],
          ["canonicalRoleName", "roleName", "name", "label", "value"]
        ),
        onError: (error) => {
          setCanonicalRoleOptions([]);
          setBanner((current) => current || {
            type: "error",
            msg: error.message || i18n.t("onboarding.errors.unableToLoadCanonicalRoles")
          });
        }
      }),
      loadLookup({
        key: "skills",
        endpoint: SKILLS_ENDPOINT,
        setOptions: setSkillOptions,
        setLoading: setSkillsLoading,
        mapOptions: (data) => normalizeLookupOptions(data, ["skillId", "id", "value"], ["skillName", "name", "label", "value"]),
        onError: (error) => {
          setSkillOptions([]);
          setBanner((current) => current || {
            type: "error",
            msg: error.message || i18n.t("onboarding.errors.unableToLoadSkills")
          });
        }
      }),
      loadLookup({
        key: "gradeLevels",
        endpoint: GRADE_LEVELS_ENDPOINT,
        setOptions: setGradeLevelOptions,
        setLoading: setGradeLevelsLoading,
        mapOptions: (data) => normalizeLookupOptions(data, ["gradeLevelId", "id", "value"], ["gradeLevelName", "name", "label", "value"]),
        onError: (error) => {
          setGradeLevelOptions([]);
          setBanner((current) => current || {
            type: "error",
            msg: error.message || i18n.t("onboarding.errors.unableToLoadGradeLevels")
          });
        }
      }),
      loadLookup({
        key: "divisions",
        endpoint: DIVISIONS_ENDPOINT,
        setOptions: setDivisionOptions,
        setLoading: setDivisionsLoading,
        mapOptions: (data) => normalizeLookupOptions(data, ["divisionId", "id", "value"], ["divisionName", "name", "label", "value"]),
        onError: (error) => {
          setDivisionOptions([]);
          setBanner((current) => current || {
            type: "error",
            msg: error.message || i18n.t("onboarding.errors.unableToLoadDivisions")
          });
        }
      }),
      loadLookup({
        key: "costCenters",
        endpoint: COST_CENTERS_ENDPOINT,
        setOptions: setCostCenterOptions,
        setLoading: setCostCentersLoading,
        mapOptions: (data) => normalizeLookupOptions(data, ["costCenterId", "id", "value"], ["costCenterName", "name", "label", "value"]),
        onError: (error) => {
          setCostCenterOptions([]);
          setBanner((current) => current || {
            type: "error",
            msg: error.message || i18n.t("onboarding.errors.unableToLoadCostCenters")
          });
        }
      }),
      loadLookup({
        key: "workLocations",
        endpoint: WORK_LOCATIONS_ENDPOINT,
        setOptions: setWorkLocationOptions,
        setLoading: setWorkLocationsLoading,
        mapOptions: (data) => normalizeLookupOptions(
          data,
          ["workLocationId", "locationId", "id", "value"],
          ["workLocationName", "locationName", "name", "label", "value"]
        ),
        onError: (error) => {
          setWorkLocationOptions([]);
          setBanner((current) => current || {
            type: "error",
            msg: error.message || i18n.t("onboarding.errors.unableToLoadWorkLocations")
          });
        }
      }),
      loadLookup({
        key: "designations",
        endpoint: DESIGNATIONS_ENDPOINT,
        setOptions: setDesignationOptions,
        setLoading: setDesignationsLoading,
        mapOptions: (data) => normalizeLookupOptions(data, ["designationId", "id", "value"], ["designationName", "name", "label", "value"]),
        onError: (error) => {
          setDesignationOptions([]);
          setBanner((current) => current || {
            type: "error",
            msg: error.message || i18n.t("onboarding.errors.unableToLoadDesignations")
          });
        }
      }),
      loadLookup({
        key: "departments",
        endpoint: DEPARTMENTS_ENDPOINT,
        setOptions: setDepartmentOptions,
        setLoading: setDepartmentsLoading,
        mapOptions: (data) => normalizeLookupOptions(
          data,
          ["departmentId", "deptId", "id", "value"],
          ["departmentName", "deptName", "name", "label", "value"]
        ),
        onError: (error) => {
          setDepartmentOptions([]);
          setBanner((current) => current || {
            type: "error",
            msg: error.message || i18n.t("onboarding.errors.unableToLoadDepartments")
          });
        }
      }),
      loadLookup({
        key: "empTypes",
        endpoint: EMP_TYPES_ENDPOINT,
        setOptions: setEmpTypeOptions,
        setLoading: setEmpTypesLoading,
        mapOptions: (data) => {
          // /org/emp-type/ returns { typeId, type_Name, ... }. empType is stored
          // by name (not id) in current-employment, so value === label === type_Name.
          const options = normalizeEnumOptions(data, ["type_Name", "typeName"], ["type_Name", "typeName"]);
          return options.length ? translateEmpTypeOptions(options) : translateEmpTypeOptions(EMP_TYPES.map((value) => ({ value, label: value })));
        },
        onError: () => {
          setEmpTypeOptions(translateEmpTypeOptions(EMP_TYPES.map((value) => ({ value, label: value }))));
        },
        cacheOnError: true
      }),
      loadLookup({
        key: "empStatuses",
        endpoint: EMP_STATUSES_ENDPOINT,
        setOptions: setEmpStatusOptions,
        setLoading: setEmpStatusesLoading,
        mapOptions: (data) => {
          // /org/emp-status/ returns { statusId, status_Name, ... }. empStatus is
          // stored by name (not id), so value === label === status_Name.
          const options = normalizeEnumOptions(data, ["status_Name", "statusName"], ["status_Name", "statusName"]);
          return options.length ? translateEmpStatusOptions(options) : translateEmpStatusOptions(EMP_STATUSES.map((value) => ({ value, label: value })));
        },
        onError: () => {
          setEmpStatusOptions(translateEmpStatusOptions(EMP_STATUSES.map((value) => ({ value, label: value }))));
        },
        cacheOnError: true
      })
    ]);

    return () => {
      cancelled = true;
    };
  }, [isActive, tenantId]);

  function resetFeedback() {
    setBanner(null);
  }

  function validateField(name, value, touchMap = touched) {
    const requiredMessage = requiredFields.has(name) && !String(value || "").trim() ? i18n.t("validation.fieldRequired", { field: getFieldLabel(name) }) : "";
    const ruleMessage = RULES[name] ? RULES[name](value || "", { ...form, [name]: value }) : "";
    const message = requiredMessage || ruleMessage;
    setErrors((prev) => ({ ...prev, [name]: touchMap[name] ? message : "" }));
    return !message;
  }

  function validateAll() {
    const nextTouched = {};
    const nextErrors = {};
    let valid = true;

    Object.keys(RULES).forEach((name) => {
      nextTouched[name] = true;
      const requiredMessage = requiredFields.has(name) && !String(form[name] || "").trim() ? i18n.t("validation.fieldRequired", { field: getFieldLabel(name) }) : "";
      const ruleMessage = RULES[name] ? RULES[name](form[name] || "", form) : "";
      const message = requiredMessage || ruleMessage;
      nextErrors[name] = message;
      if (message) valid = false;
    });

    setTouched(nextTouched);
    setErrors(nextErrors);
    return valid;
  }

  function handleActionChange(nextAction) {
    setAction(nextAction);
    setErrors({});
    setTouched({});
    resetFeedback();
  }

  function handleInputChange(event) {
    const { name, value } = event.target;
    const nextValue = name === "dateOfJoin" || name === "terminationDate" ? formatDisplayDateInput(value) : value;
    setForm((prev) => ({
      ...prev,
      [name]: nextValue
    }));
    if (touched[name]) validateField(name, nextValue);
  }

  function handleInputBlur(event) {
    const { name, value } = event.target;
    const nextTouched = { ...touched, [name]: true };
    setTouched(nextTouched);
    validateField(name, value, nextTouched);
  }

  async function hydrateRoleName(roleId) {
    if (!tenantId || !roleId || roleOptions.some((option) => option.value === String(roleId))) return;

    try {
      const data = await fetchOrgLookupById(ROLE_BY_ID_ENDPOINT, roleId);
      if (!data) return;
      const roleLabel = extractLookupLabel(data, ["roleName", "name", "label", "value"]);
      if (!roleLabel) return;
      setRoleOptions((current) => current.some((option) => option.value === String(roleId))
        ? current
        : [...current, { value: String(roleId), label: roleLabel }]);
    } catch {}
  }

  async function hydrateDesignationOption(designationId) {
    if (!tenantId || !designationId || designationOptions.some((option) => option.value === String(designationId))) return;

    try {
      const data = await fetchOrgLookupById(DESIGNATION_BY_ID_ENDPOINT, designationId);
      if (!data) return;
      const label = extractLookupLabel(data, ["designationName", "name", "label", "value"]);
      if (!label) return;
      setDesignationOptions((current) => current.some((option) => option.value === String(designationId))
        ? current
        : [...current, { value: String(designationId), label }]);
    } catch {}
  }

  async function hydrateDepartmentOption(departmentId) {
    if (!tenantId || !departmentId || departmentOptions.some((option) => option.value === String(departmentId))) return;

    try {
      const data = await fetchOrgLookupById(DEPARTMENT_BY_ID_ENDPOINT, departmentId);
      if (!data) return;
      const label = extractLookupLabel(data, ["departmentName", "deptName", "name", "label", "value"]);
      if (!label) return;
      setDepartmentOptions((current) => current.some((option) => option.value === String(departmentId))
        ? current
        : [...current, { value: String(departmentId), label }]);
    } catch {}
  }

  async function hydrateGradeLevelOption(gradeLevelId) {
    if (!tenantId || !gradeLevelId || gradeLevelOptions.some((option) => option.value === String(gradeLevelId))) return;

    try {
      const data = await fetchOrgLookupById(GRADE_LEVEL_BY_ID_ENDPOINT, gradeLevelId);
      if (!data) return;
      const label = extractLookupLabel(data, ["gradeLevelName", "name", "label", "value"]);
      if (!label) return;
      setGradeLevelOptions((current) => current.some((option) => option.value === String(gradeLevelId))
        ? current
        : [...current, { value: String(gradeLevelId), label }]);
    } catch {}
  }

  async function hydrateDivisionOption(divisionId) {
    if (!tenantId || !divisionId || divisionOptions.some((option) => option.value === String(divisionId))) return;

    try {
      const data = await fetchOrgLookupById(DIVISION_BY_ID_ENDPOINT, divisionId);
      if (!data) return;
      const label = extractLookupLabel(data, ["divisionName", "name", "label", "value"]);
      if (!label) return;
      setDivisionOptions((current) => current.some((option) => option.value === String(divisionId))
        ? current
        : [...current, { value: String(divisionId), label }]);
    } catch {}
  }

  async function hydrateCostCenterOption(costCenterId) {
    if (!tenantId || !costCenterId || costCenterOptions.some((option) => option.value === String(costCenterId))) return;

    try {
      const data = await fetchOrgLookupById(COST_CENTER_BY_ID_ENDPOINT, costCenterId);
      if (!data) return;
      const label = extractLookupLabel(data, ["costCenterName", "name", "label", "value"]);
      if (!label) return;
      setCostCenterOptions((current) => current.some((option) => option.value === String(costCenterId))
        ? current
        : [...current, { value: String(costCenterId), label }]);
    } catch {}
  }

  async function hydrateWorkLocationOption(workLocationId) {
    if (!tenantId || !workLocationId || workLocationOptions.some((option) => option.value === String(workLocationId))) return;

    try {
      const data = await fetchOrgLookupById(WORK_LOCATION_BY_ID_ENDPOINT, workLocationId);
      if (!data) return;
      const label = extractLookupLabel(data, ["workLocationName", "locationName", "name", "label", "value"]);
      if (!label) return;
      setWorkLocationOptions((current) => current.some((option) => option.value === String(workLocationId))
        ? current
        : [...current, { value: String(workLocationId), label }]);
    } catch {}
  }

  async function hydrateSkillOption(skillId) {
    if (!tenantId || !skillId || skillOptions.some((option) => option.value === String(skillId))) return;

    try {
      const data = await fetchOrgLookupById(SKILL_BY_ID_ENDPOINT, skillId);
      if (!data) return;
      const label = extractLookupLabel(data, ["skillName", "name", "label", "value"]);
      if (!label) return;
      setSkillOptions((current) => current.some((option) => option.value === String(skillId))
        ? current
        : [...current, { value: String(skillId), label }]);
    } catch {}
  }

  useEffect(() => {
    if (!isActive || selectedRoleIds.length === 0) return;
    selectedRoleIds.forEach((roleId) => {
      if (!roleOptions.some((option) => option.value === roleId)) {
        hydrateRoleName(roleId);
      }
    });
  }, [isActive, selectedRoleIds, roleOptions]);

  useEffect(() => {
    if (!isActive || !form.designationId) return;
    if (designationOptions.some((option) => option.value === form.designationId)) return;
    hydrateDesignationOption(form.designationId);
  }, [isActive, designationOptions, form.designationId]);

  useEffect(() => {
    if (!isActive || !form.departmentId) return;
    if (departmentOptions.some((option) => option.value === form.departmentId)) return;
    hydrateDepartmentOption(form.departmentId);
  }, [isActive, departmentOptions, form.departmentId]);

  useEffect(() => {
    if (!isActive || !form.gradeLevelId) return;
    if (gradeLevelOptions.some((option) => option.value === form.gradeLevelId)) return;
    hydrateGradeLevelOption(form.gradeLevelId);
  }, [isActive, form.gradeLevelId, gradeLevelOptions]);

  useEffect(() => {
    if (!isActive || !form.divisionId) return;
    if (divisionOptions.some((option) => option.value === form.divisionId)) return;
    hydrateDivisionOption(form.divisionId);
  }, [isActive, divisionOptions, form.divisionId]);

  useEffect(() => {
    if (!isActive || !form.costCenterId) return;
    if (costCenterOptions.some((option) => option.value === form.costCenterId)) return;
    hydrateCostCenterOption(form.costCenterId);
  }, [isActive, costCenterOptions, form.costCenterId]);

  useEffect(() => {
    if (!isActive || !form.workLocationId) return;
    if (workLocationOptions.some((option) => option.value === form.workLocationId)) return;
    hydrateWorkLocationOption(form.workLocationId);
  }, [isActive, workLocationOptions, form.workLocationId]);

  useEffect(() => {
    if (!isActive || selectedSkillIds.length === 0) return;
    selectedSkillIds.forEach((skillId) => {
      if (!skillOptions.some((option) => option.value === skillId)) {
        hydrateSkillOption(skillId);
      }
    });
  }, [isActive, selectedSkillIds, skillOptions]);

  function toggleSkill(skillId) {
    const nextValues = selectedSkillIds.includes(skillId)
      ? selectedSkillIds.filter((value) => value !== skillId)
      : [...selectedSkillIds, skillId];

    const nextSerialized = nextValues.join(",");
    setForm((prev) => ({ ...prev, skillId: nextSerialized }));
    if (touched.skillId) validateField("skillId", nextSerialized);
  }

  function toggleCanonicalRole(roleName) {
    const nextValues = selectedCanonicalRoleNames.includes(roleName)
      ? selectedCanonicalRoleNames.filter((v) => v !== roleName)
      : [...selectedCanonicalRoleNames, roleName];
    const nextSerialized = nextValues.join(",");
    setForm((prev) => ({ ...prev, canonicalRoleName: nextSerialized }));
    if (touched.canonicalRoleName) validateField("canonicalRoleName", nextSerialized);
  }

  function toggleRole(roleId) {
    const nextValues = selectedRoleIds.includes(roleId)
      ? selectedRoleIds.filter((value) => value !== roleId)
      : [...selectedRoleIds, roleId];
    const nextSerializedIds = nextValues.join(",");

    setForm((prev) => ({
      ...prev,
      roleId: nextSerializedIds
    }));

    if (touched.roleId) validateField("roleId", nextSerializedIds);
  }

  async function submitMutation() {
    if (!validateAll()) {
      return;
    }

    setLoading(true);
    resetFeedback();

    try {
      if (!currentAction.isAvailable) {
        throw new Error(buildOnboardingAccessMessage(i18n.t("common.currentEmployment"), currentAction.title || action));
      }

      const resolvedRoleIds = selectedRoleIds.length > 0
        ? selectedRoleIds.join(",")
        : form.roleId.trim();

      const payload = {
        empId: Number(form.empId.trim()),
        empType: form.empType || undefined,
        empStatus: form.empStatus || undefined,
        roleId: resolvedRoleIds || undefined,
        canonicalRoleName: form.canonicalRoleName.trim() || undefined,
        roleName: form.canonicalRoleName.trim() || undefined,
        skillId: form.skillId.trim() || undefined,
        skills: selectedSkillIds.length > 0 ? selectedSkillIds : (form.skillId.trim() || undefined),
        designationId: form.designationId ? Number(form.designationId) : undefined,
        departmentId: form.departmentId ? Number(form.departmentId) : undefined,
        gradeLevelId: form.gradeLevelId ? Number(form.gradeLevelId) : undefined,
        divisionId: form.divisionId ? Number(form.divisionId) : undefined,
        costCenterId: form.costCenterId ? Number(form.costCenterId) : undefined,
        workLocationId: form.workLocationId ? Number(form.workLocationId) : undefined,
        loginBasedOnGps: form.loginBasedOnGps,
        gpsLogin: form.loginBasedOnGps === "Y",
        isGpsLogin: form.loginBasedOnGps === "Y",
        managerId: form.managerId.trim() ? Number(form.managerId.trim()) : undefined,
        managerName: form.managerName.trim() || undefined,
        hrId: form.hrId.trim() ? Number(form.hrId.trim()) : undefined,
        hrName: form.hrName.trim() || undefined,
        dateOfJoin: displayDateToApiDate(form.dateOfJoin),
        terminationDate: displayDateToApiDate(form.terminationDate)
      };

      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined || payload[key] === "") delete payload[key];
      });
      console.log("pay:",JSON.stringify(payload))
      const response = await fetch(resolveApiUrl(buildUrl(currentAction.endpoint, form)), {
        method: currentAction.method,
        headers: {
          "Content-Type": "application/json",
          "x-tenant-code": tenantCode || "",
          "x-tenant-id": String(tenantId || ""),
          "x-service-code": currentAction.serviceCode,
          authorization: `Bearer ${sessionAccessToken}`
        },
        body: JSON.stringify(payload)

      });
      console.log("res:",response.ok)
      if (!response.ok) {
        const { errorMessage, errorBody } = await parseOnboardingErrorResponse(response, getFieldLabelsMap());
        handleOnboardingApiError(errorBody, errorMessage, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
        return;
      }

      const data = await response.json().catch(() => ({ message: i18n.t("onboarding.messages.saveCurrentEmploymentCompleted") }));
      setBanner({
        type: "success",
        msg: data.message || (action === "add"
          ? i18n.t("onboarding.messages.saveCurrentEmploymentCompleted")
          : i18n.t("onboarding.messages.actionCompletedSuccessfully", { action: i18n.t("common.update") }))
      });
      if (action === "update") {
        await fetchEmployment();
      }
      if (onSuccess) onSuccess(data);
    } catch (error) {
      handleOnboardingApiError(null, error.message, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
    } finally {
      setLoading(false);
    }
  }

  async function runSearch() {
    setLoading(true);
    resetFeedback();

    try {
      if (!currentAction.isAvailable) {
        throw new Error(buildOnboardingAccessMessage(i18n.t("common.currentEmployment"), currentAction.title || action));
      }

      const response = await fetch(resolveApiUrl(currentAction.endpoint), {
        method: currentAction.method,
        headers: {
          "Content-Type": "application/json",
          "x-tenant-code": tenantCode || "",
          "x-tenant-id": String(tenantId || ""),
          "x-service-code": currentAction.serviceCode,
          authorization: `Bearer ${sessionAccessToken}`
        },
        body: JSON.stringify({
          empId: form.empId.trim() ? Number(form.empId.trim()) : undefined,
          empType: form.empType || undefined,
          empStatus: form.empStatus || undefined,
          canonicalRoleName: form.canonicalRoleName.trim() || undefined,
          managerId: form.managerId.trim() ? Number(form.managerId.trim()) : undefined,
          managerName: form.managerName.trim() || undefined,
          hrId: form.hrId.trim() ? Number(form.hrId.trim()) : undefined,
          hrName: form.hrName.trim() || undefined
        })
      });

      if (!response.ok) {
        const { errorMessage, errorBody } = await parseOnboardingErrorResponse(response, getFieldLabelsMap());
        handleOnboardingApiError(errorBody, errorMessage, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
        return;
      }

      await response.json();
      setBanner({ type: "success", msg: i18n.t("onboarding.messages.currentEmploymentResultsLoaded") });
    } catch (error) {
      handleOnboardingApiError(null, error.message, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
    } finally {
      setLoading(false);
    }
  }

  async function runHierarchy() {
    if (!validateAll()) {
      return;
    }

    setLoading(true);
    resetFeedback();

    try {
      if (!currentAction.isAvailable) {
        throw new Error(buildOnboardingAccessMessage(i18n.t("common.currentEmployment"), currentAction.title || action));
      }

      const response = await fetch(resolveApiUrl(buildUrl(currentAction.endpoint, form)), {
        method: currentAction.method,
        headers: {
          "Content-Type": "application/json",
          "x-tenant-code": tenantCode || "",
          "x-tenant-id": String(tenantId || ""),
          "x-service-code": currentAction.serviceCode,
          authorization: `Bearer ${sessionAccessToken}`
        }
      });

      if (!response.ok) {
        const { errorMessage, errorBody } = await parseOnboardingErrorResponse(response, getFieldLabelsMap());
        handleOnboardingApiError(errorBody, errorMessage, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
        return;
      }

      await response.json();
      setBanner({ type: "success", msg: i18n.t("onboarding.messages.employmentHierarchyLoaded") });
    } catch (error) {
      handleOnboardingApiError(null, error.message, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
    } finally {
      setLoading(false);
    }
  }

  function handleSave() {
    if (action === "search") {
      runSearch();
      return;
    }
    if (action === "hierarchy") {
      runHierarchy();
      return;
    }
    submitMutation();
  }

  function handleNext() {
    const nextAction = getNextAction(action);
    handleActionChange(nextAction);
    if (onNext) onNext(nextAction);
  }

  return (
    <div className={`cem-page${isTabMode ? " is-tab-mode" : ""}`}>
      <main className="cem-main">
        {!isTabMode && breadcrumb && (
          <div className="cem-breadcrumb" aria-label={i18n.t("common.breadcrumb")}>
            {breadcrumb.map((item, index) => (
              <React.Fragment key={item.label}>
                {index > 0 && <span className="cem-bc-sep">/</span>}
                <button className={`cem-bc-item${item.active ? " active" : ""}`} onClick={item.active ? undefined : item.onClick} disabled={item.active}>
                  {item.label}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}

        {!isTabMode && (
          <OnboardingPageTitle
            onBack={onBack}
            employeeId={initialEmployeeId}
            titleClassName="cem-page-title"
            subtitleClassName="cem-page-sub"
            title={<Trans i18nKey="common.currentEmployment" />}
            subtitle={<Trans i18nKey="dateTime.manageEmployeeRoleReportingJoiningDetailsAndEmployment" />}
            action={
              <button className="cem-header-action" type="button" onClick={() => handleActionChange(action === "update" ? "add" : "update")}>
                {action === "update" ? i18n.t("onboarding.addNew") : i18n.t("common.update")}
              </button>
            }
          />
        )}

        <div className="cem-form-card">
          <div className="cem-section">
            <div className="cem-grid cols-3">
              <Field id="empType" label={getFieldLabel("empType")} required={requiredFields.has("empType")} error={touched.empType && errors.empType}>
                <Select
                  value={form.empType}
                  onValueChange={(value) => {
                    setForm((prev) => ({ ...prev, empType: value }));
                    if (touched.empType) validateField("empType", value);
                  }}
                  onOpenChange={(open) => {
                    if (!open) {
                      const nextTouched = { ...touched, empType: true };
                      setTouched(nextTouched);
                      validateField("empType", form.empType, nextTouched);
                    }
                  }}
                >
                  <SelectTrigger id="empType" className={`cem-select${touched.empType && errors.empType ? " error" : ""}`}>
                    <SelectValue placeholder={empTypesLoading ? i18n.t("onboarding.loadingTypes") : i18n.t("onboarding.selectType")} />
                  </SelectTrigger>
                  <SelectContent>
                    {empTypeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field id="empStatus" label={getFieldLabel("empStatus")} required={requiredFields.has("empStatus")} error={touched.empStatus && errors.empStatus}>
                <Select
                  value={form.empStatus}
                  onValueChange={(value) => {
                    setForm((prev) => ({ ...prev, empStatus: value }));
                    if (touched.empStatus) validateField("empStatus", value);
                  }}
                  onOpenChange={(open) => {
                    if (!open) {
                      const nextTouched = { ...touched, empStatus: true };
                      setTouched(nextTouched);
                      validateField("empStatus", form.empStatus, nextTouched);
                    }
                  }}
                >
                  <SelectTrigger id="empStatus" className={`cem-select${touched.empStatus && errors.empStatus ? " error" : ""}`}>
                    <SelectValue placeholder={empStatusesLoading ? i18n.t("onboarding.loadingStatuses") : i18n.t("onboarding.selectStatus")} />
                  </SelectTrigger>
                  <SelectContent>
                    {empStatusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <div className="cem-grid full">
              <Field id="roleId" label={getFieldLabel("roleId")} required={requiredFields.has("roleId")} error={touched.roleId && errors.roleId}>
                <EmploymentMultiSelect
                  id="roleId"
                  options={roleOptions}
                  selectedValues={selectedRoleIds}
                  onToggle={toggleRole}
                  loading={rolesLoading}
                  loadingText={i18n.t("onboarding.loadingRoles")}
                  emptyText={i18n.t("onboarding.noRolesAvailable")}
                  error={touched.roleId && errors.roleId}
                  onBlur={() => {
                    const nextTouched = { ...touched, roleId: true };
                    setTouched(nextTouched);
                    validateField("roleId", form.roleId, nextTouched);
                  }}
                />
              </Field>
            </div>

            <div className="cem-grid full">
              <Field id="canonicalRoleName" label={getFieldLabel("canonicalRoleName")} required={requiredFields.has("canonicalRoleName")} error={touched.canonicalRoleName && errors.canonicalRoleName}>
                <EmploymentMultiSelect
                  id="canonicalRoleName"
                  options={canonicalRoleOptions.map((option) => ({ value: option.label, label: option.label }))}
                  selectedValues={selectedCanonicalRoleNames}
                  onToggle={toggleCanonicalRole}
                  loading={canonicalRolesLoading}
                  loadingText={i18n.t("onboarding.loadingCanonicalRoles")}
                  emptyText={i18n.t("onboarding.noCanonicalRolesAvailable")}
                  error={touched.canonicalRoleName && errors.canonicalRoleName}
                  onBlur={() => {
                    const nextTouched = { ...touched, canonicalRoleName: true };
                    setTouched(nextTouched);
                    validateField("canonicalRoleName", form.canonicalRoleName, nextTouched);
                  }}
                />
              </Field>
            </div>

            <div className="cem-grid cols-3">
              <Field id="hrId" label={getFieldLabel("hrId")} required={requiredFields.has("hrId")} error={touched.hrId && errors.hrId}>
                <Select
                  value={form.hrId || undefined}
                  onValueChange={(value) => {
                    const option = hrOptions.find((entry) => entry.value === value);
                    setForm((prev) => ({
                      ...prev,
                      hrId: value,
                      hrName: option?.name || option?.label || ""
                    }));
                    if (touched.hrId) validateField("hrId", value);
                  }}
                  onOpenChange={(open) => {
                    if (!open) {
                      const nextTouched = { ...touched, hrId: true };
                      setTouched(nextTouched);
                      validateField("hrId", form.hrId, nextTouched);
                    }
                  }}
                  disabled={hrsLoading && hrOptions.length === 0}
                >
                  <SelectTrigger id="hrId" className={`cem-select${touched.hrId && errors.hrId ? " error" : ""}`}>
                    <SelectValue
                      placeholder={
                        hrsLoading
                          ? i18n.t("common.loading")
                          : i18n.t("formActions.searchHr")
                      }
                    >
                      {hrOptions.find((option) => option.value === form.hrId)?.label
                        || form.hrName
                        || undefined}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent
                    searchPlaceholder={i18n.t("formActions.searchHr")}
                    viewportClassName="h-auto max-h-60 overflow-y-auto"
                    onViewportScroll={handleHrScroll}
                  >
                    {hrOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value} textValue={option.label}>
                        {option.label}
                      </SelectItem>
                    ))}
                    {hrsLoadingMore ? (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground text-center">
                        {i18n.t("common.loading")}
                      </div>
                    ) : null}
                  </SelectContent>
                </Select>
              </Field>
              <Field id="managerId" label={getFieldLabel("managerId")} required={requiredFields.has("managerId")} error={touched.managerId && errors.managerId}>
                <Select
                  value={form.managerId || undefined}
                  onValueChange={(value) => {
                    const option = managerOptions.find((entry) => entry.value === value);
                    setForm((prev) => ({
                      ...prev,
                      managerId: value,
                      managerName: option?.name || option?.label || ""
                    }));
                    if (touched.managerId) validateField("managerId", value);
                  }}
                  onOpenChange={(open) => {
                    if (!open) {
                      const nextTouched = { ...touched, managerId: true };
                      setTouched(nextTouched);
                      validateField("managerId", form.managerId, nextTouched);
                    }
                  }}
                  disabled={managersLoading && managerOptions.length === 0}
                >
                  <SelectTrigger id="managerId" className={`cem-select${touched.managerId && errors.managerId ? " error" : ""}`}>
                    <SelectValue
                      placeholder={
                        managersLoading
                          ? i18n.t("common.loading")
                          : i18n.t("formActions.searchManager")
                      }
                    >
                      {managerOptions.find((option) => option.value === form.managerId)?.label
                        || form.managerName
                        || undefined}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent
                    searchPlaceholder={i18n.t("formActions.searchManager")}
                    viewportClassName="h-auto max-h-60 overflow-y-auto"
                    onViewportScroll={handleManagerScroll}
                  >
                    {managerOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value} textValue={option.label}>
                        {option.label}
                      </SelectItem>
                    ))}
                    {managersLoadingMore ? (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground text-center">
                        {i18n.t("common.loading")}
                      </div>
                    ) : null}
                  </SelectContent>
                </Select>
              </Field>
              <Field id="designationId" label={getFieldLabel("designationId")} required={requiredFields.has("designationId")} error={touched.designationId && errors.designationId}>
                {designationOptions.length > 0 ? (
                  <Select
                    value={form.designationId}
                    onValueChange={(value) => {
                      setForm((prev) => ({ ...prev, designationId: value }));
                      if (touched.designationId) validateField("designationId", value);
                    }}
                    onOpenChange={(open) => {
                      if (!open) {
                        const nextTouched = { ...touched, designationId: true };
                        setTouched(nextTouched);
                        validateField("designationId", form.designationId, nextTouched);
                      }
                    }}
                    disabled={designationsLoading || designationOptions.length === 0}
                  >
                    <SelectTrigger id="designationId" className={`cem-select${touched.designationId && errors.designationId ? " error" : ""}`}>
                      <SelectValue placeholder={designationsLoading ? i18n.t("onboarding.loadingDesignations") : designationOptions.length ? i18n.t("onboarding.selectDesignation") : i18n.t("onboarding.noDesignationsAvailable")} />
                    </SelectTrigger>
                    <SelectContent>
                      {designationOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <input id="designationId" name="designationId" value={form.designationId} onChange={handleInputChange} onBlur={handleInputBlur} className={`cem-input${touched.designationId && errors.designationId ? " error" : ""}`} placeholder={i18n.t("onboarding.enterDesignationId")} />
                )}
              </Field>
            </div>

            <div className="cem-grid cols-3">
              <Field id="departmentId" label={getFieldLabel("departmentId")} required={requiredFields.has("departmentId")} error={touched.departmentId && errors.departmentId}>
                {departmentOptions.length > 0 ? (
                  <Select
                    value={form.departmentId}
                    onValueChange={(value) => {
                      setForm((prev) => ({ ...prev, departmentId: value }));
                      if (touched.departmentId) validateField("departmentId", value);
                    }}
                    onOpenChange={(open) => {
                      if (!open) {
                        const nextTouched = { ...touched, departmentId: true };
                        setTouched(nextTouched);
                        validateField("departmentId", form.departmentId, nextTouched);
                      }
                    }}
                    disabled={departmentsLoading || departmentOptions.length === 0}
                  >
                    <SelectTrigger id="departmentId" className={`cem-select${touched.departmentId && errors.departmentId ? " error" : ""}`}>
                      <SelectValue placeholder={departmentsLoading ? i18n.t("onboarding.loadingDepartments") : departmentOptions.length ? i18n.t("onboarding.selectDepartment") : i18n.t("onboarding.noDepartmentsAvailable")} />
                    </SelectTrigger>
                    <SelectContent>
                      {departmentOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <input id="departmentId" name="departmentId" value={form.departmentId} onChange={handleInputChange} onBlur={handleInputBlur} className={`cem-input${touched.departmentId && errors.departmentId ? " error" : ""}`} placeholder={i18n.t("onboarding.enterDepartmentId")} />
                )}
              </Field>
              <Field id="gradeLevelId" label={getFieldLabel("gradeLevelId")} required={requiredFields.has("gradeLevelId")} error={touched.gradeLevelId && errors.gradeLevelId}>
                {gradeLevelOptions.length > 0 ? (
                  <Select
                    value={form.gradeLevelId}
                    onValueChange={(value) => {
                      setForm((prev) => ({ ...prev, gradeLevelId: value }));
                      if (touched.gradeLevelId) validateField("gradeLevelId", value);
                    }}
                    onOpenChange={(open) => {
                      if (!open) {
                        const nextTouched = { ...touched, gradeLevelId: true };
                        setTouched(nextTouched);
                        validateField("gradeLevelId", form.gradeLevelId, nextTouched);
                      }
                    }}
                    disabled={gradeLevelsLoading || gradeLevelOptions.length === 0}
                  >
                    <SelectTrigger id="gradeLevelId" className={`cem-select${touched.gradeLevelId && errors.gradeLevelId ? " error" : ""}`}>
                      <SelectValue placeholder={gradeLevelsLoading ? i18n.t("onboarding.loadingGradeLevels") : gradeLevelOptions.length ? i18n.t("onboarding.selectGradeLevel") : i18n.t("onboarding.noGradeLevelsAvailable")} />
                    </SelectTrigger>
                    <SelectContent>
                      {gradeLevelOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <input id="gradeLevelId" name="gradeLevelId" value={form.gradeLevelId} onChange={handleInputChange} onBlur={handleInputBlur} className={`cem-input${touched.gradeLevelId && errors.gradeLevelId ? " error" : ""}`} placeholder={i18n.t("onboarding.enterGradeLevelId")} />
                )}
              </Field>
              <Field id="divisionId" label={getFieldLabel("divisionId")} required={requiredFields.has("divisionId")} error={touched.divisionId && errors.divisionId}>
                {divisionOptions.length > 0 ? (
                  <Select
                    value={form.divisionId}
                    onValueChange={(value) => {
                      setForm((prev) => ({ ...prev, divisionId: value }));
                      if (touched.divisionId) validateField("divisionId", value);
                    }}
                    onOpenChange={(open) => {
                      if (!open) {
                        const nextTouched = { ...touched, divisionId: true };
                        setTouched(nextTouched);
                        validateField("divisionId", form.divisionId, nextTouched);
                      }
                    }}
                    disabled={divisionsLoading || divisionOptions.length === 0}
                  >
                    <SelectTrigger id="divisionId" className={`cem-select${touched.divisionId && errors.divisionId ? " error" : ""}`}>
                      <SelectValue placeholder={divisionsLoading ? i18n.t("onboarding.loadingDivisions") : divisionOptions.length ? i18n.t("onboarding.selectDivision") : i18n.t("onboarding.noDivisionsAvailable")} />
                    </SelectTrigger>
                    <SelectContent>
                      {divisionOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <input id="divisionId" name="divisionId" value={form.divisionId} onChange={handleInputChange} onBlur={handleInputBlur} className={`cem-input${touched.divisionId && errors.divisionId ? " error" : ""}`} placeholder={i18n.t("onboarding.enterDivisionId")} />
                )}
              </Field>
            </div>

            <div className="cem-grid">
              <Field id="costCenterId" label={getFieldLabel("costCenterId")} required={requiredFields.has("costCenterId")} error={touched.costCenterId && errors.costCenterId}>
                {costCenterOptions.length > 0 ? (
                  <Select
                    value={form.costCenterId}
                    onValueChange={(value) => {
                      setForm((prev) => ({ ...prev, costCenterId: value }));
                      if (touched.costCenterId) validateField("costCenterId", value);
                    }}
                    onOpenChange={(open) => {
                      if (!open) {
                        const nextTouched = { ...touched, costCenterId: true };
                        setTouched(nextTouched);
                        validateField("costCenterId", form.costCenterId, nextTouched);
                      }
                    }}
                    disabled={costCentersLoading || costCenterOptions.length === 0}
                  >
                    <SelectTrigger id="costCenterId" className={`cem-select${touched.costCenterId && errors.costCenterId ? " error" : ""}`}>
                      <SelectValue placeholder={costCentersLoading ? i18n.t("onboarding.loadingCostCenters") : costCenterOptions.length ? i18n.t("onboarding.selectCostCenter") : i18n.t("onboarding.noCostCentersAvailable")} />
                    </SelectTrigger>
                    <SelectContent>
                      {costCenterOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <input id="costCenterId" name="costCenterId" value={form.costCenterId} onChange={handleInputChange} onBlur={handleInputBlur} className={`cem-input${touched.costCenterId && errors.costCenterId ? " error" : ""}`} placeholder={i18n.t("onboarding.enterCostCenterId")} />
                )}
              </Field>
              <Field id="workLocationId" label={getFieldLabel("workLocationId")} required={requiredFields.has("workLocationId")} error={touched.workLocationId && errors.workLocationId}>
                {workLocationOptions.length > 0 ? (
                  <Select
                    value={form.workLocationId}
                    onValueChange={(value) => {
                      setForm((prev) => ({ ...prev, workLocationId: value }));
                      if (touched.workLocationId) validateField("workLocationId", value);
                    }}
                    onOpenChange={(open) => {
                      if (!open) {
                        const nextTouched = { ...touched, workLocationId: true };
                        setTouched(nextTouched);
                        validateField("workLocationId", form.workLocationId, nextTouched);
                      }
                    }}
                    disabled={workLocationsLoading || workLocationOptions.length === 0}
                  >
                    <SelectTrigger
                      id="workLocationId"
                      className={`cem-select${touched.workLocationId && errors.workLocationId ? " error" : ""}`}
                    >
                      <SelectValue
                        placeholder={
                          workLocationsLoading
                            ? i18n.t("onboarding.loadingWorkLocations")
                            : workLocationOptions.length
                              ? i18n.t("onboarding.selectWorkLocation")
                              : i18n.t("onboarding.noWorkLocationsAvailable")
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {workLocationOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <input
                    id="workLocationId"
                    name="workLocationId"
                    value={form.workLocationId}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`cem-input${touched.workLocationId && errors.workLocationId ? " error" : ""}`}
                    placeholder={i18n.t("onboarding.enterWorkLocationId")}
                  />
                )}
              </Field>
            </div>

            <div className="cem-grid">
              <Field id="dateOfJoin" label={getFieldLabel("dateOfJoin")} required={requiredFields.has("dateOfJoin")} error={touched.dateOfJoin && errors.dateOfJoin}>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={`cem-input${touched.dateOfJoin && errors.dateOfJoin ? " error" : ""} w-full h-auto justify-start text-left font-normal`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 per-file-hint" />
                      {form.dateOfJoin ? form.dateOfJoin : <span className="text-muted-foreground">{i18n.t("onboarding.ddMmYyyy")}</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
                    <Calendar
                      mode="single"
                      captionLayout="dropdown"
                      startMonth={new Date(1990, 0)}
                      endMonth={new Date(2100, 0)}
                      selected={/^\d{2}-\d{2}-\d{4}$/.test(form.dateOfJoin) ? parse(form.dateOfJoin, "dd-MM-yyyy", new Date()) : undefined}
                      onSelect={(date) => {
                        if (!date) return;
                        const v = format(date, "dd-MM-yyyy");
                        setForm((prev) => ({ ...prev, dateOfJoin: v }));
                        const nextTouched = { ...touched, dateOfJoin: true };
                        setTouched(nextTouched);
                        validateField("dateOfJoin", v, nextTouched);
                        if (form.terminationDate) validateField("terminationDate", form.terminationDate, nextTouched);
                      }}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </Field>
              <Field id="terminationDate" label={getFieldLabel("terminationDate")} error={touched.terminationDate && errors.terminationDate}>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={`cem-input${touched.terminationDate && errors.terminationDate ? " error" : ""} w-full h-auto justify-start text-left font-normal`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 per-file-hint" />
                      {form.terminationDate ? form.terminationDate : <span className="text-muted-foreground">{i18n.t("onboarding.ddMmYyyy")}</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
                    <Calendar
                      mode="single"
                      captionLayout="dropdown"
                      startMonth={new Date(1990, 0)}
                      endMonth={new Date(2100, 0)}
                      selected={/^\d{2}-\d{2}-\d{4}$/.test(form.terminationDate) ? parse(form.terminationDate, "dd-MM-yyyy", new Date()) : undefined}
                      disabled={
                        /^\d{2}-\d{2}-\d{4}$/.test(form.dateOfJoin)
                          ? { before: parse(form.dateOfJoin, "dd-MM-yyyy", new Date()) }
                          : undefined
                      }
                      onSelect={(date) => {
                        if (!date) return;
                        const v = format(date, "dd-MM-yyyy");
                        setForm((prev) => ({ ...prev, terminationDate: v }));
                        const nextTouched = { ...touched, terminationDate: true };
                        setTouched(nextTouched);
                        validateField("terminationDate", v, nextTouched);
                      }}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </Field>
            </div>

            <div className="cem-grid full">
              <Field id="skillId" label={getFieldLabel("skillId")} required={requiredFields.has("skillId")} error={touched.skillId && errors.skillId}>
                {skillOptions.length > 0 || skillsLoading ? (
                  <EmploymentMultiSelect
                    id="skillId"
                    options={skillOptions}
                    selectedValues={selectedSkillIds}
                    onToggle={toggleSkill}
                    loading={skillsLoading}
                    loadingText={i18n.t("onboarding.loadingSkills")}
                    emptyText={i18n.t("onboarding.noSkillsAvailable", { defaultValue: "No skills available" })}
                    error={touched.skillId && errors.skillId}
                    onBlur={() => {
                      const nextTouched = { ...touched, skillId: true };
                      setTouched(nextTouched);
                      validateField("skillId", form.skillId, nextTouched);
                    }}
                  />
                ) : (
                  <input id="skillId" name="skillId" value={form.skillId} onChange={handleInputChange} onBlur={handleInputBlur} className={`cem-input${touched.skillId && errors.skillId ? " error" : ""}`} placeholder={i18n.t("onboarding.enterSkillIdsCommaSeparated")} />
                )}
              </Field>
            </div>
          </div>

          <div className="cem-section">
            {banner && <div className={`cem-banner ${banner.type}`}>{banner.msg}</div>}
            {!isTabMode && (
              <div className="cem-actions">
                <button className="cem-primary-btn" type="button" onClick={handleSave} disabled={loading || !currentAction.isAvailable}>
                  {loading
                    ? i18n.t("localConveyance.saving")
                    : action === "search"
                      ? i18n.t("common.search")
                      : action === "hierarchy"
                        ? i18n.t("onboarding.getHierarchy")
                        : i18n.t("formActions.save")}
                </button>
                <button className="cem-secondary-btn" type="button" onClick={handleNext}>{i18n.t("pagination.next2")}</button>
              </div>
            )}
          </div>
        </div>

      </main>
    </div>
  );
}
